/**
 * Supabase data-access layer (B3). Import from "@/lib/db".
 *
 * Backend-less: typed thin repos over `getSupabase()`. Rows map 1:1 to the
 * snake_case domain types in `@/types` (no camelCase mapping — CONTRACT §1/§5).
 *
 * ENV-SAFE: every call goes through `getSupabase()` which only throws when the
 * client is actually used without `.env`. Importing this module never throws,
 * so the app builds without `.env` (task #4).
 */

import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";
import type {
  AppStateRow,
  ChecklistItem,
  ChecklistTemplate,
  ContractRow,
  ContractSale,
  Country,
  Deal,
  DealLineItem,
  DealParty,
  DocumentRecord,
  DocumentVersion,
  Incoterm,
  KnowledgeArticle,
  Partner,
  PaymentTerm,
  Product,
  ShipperProfile,
  WorkflowStage,
  DistributorRelationship,
  DistributorAsset,
  DistributorAssetVersion,
  DistributorProvision,
  MarketingPost,
  MarketingPostTarget,
  ContractAmendment,
  ContractAmendmentInput,
  ContractAmendmentPatch,
  DistributorIssue,
  DistributorIssueEvent,
  TimelineLane,
  ContractTimelineEvent,
  TimelineEventMove,
} from "@/types";

export { isSupabaseConfigured };

/** Columns the DB generates / defaults — omit on insert. */
type NewRow<T, K extends keyof T> = Omit<T, K>;

/** Throw a uniform, actionable error when DB is hit without config. */
function assertConfigured(): void {
  if (!isSupabaseConfigured()) {
    throw new Error(
      "[db] Supabase is not configured. Set VITE_SUPABASE_URL / " +
        "VITE_SUPABASE_ANON_KEY in .env, or guard UI calls with isSupabaseConfigured().",
    );
  }
}

/** Generic select-all helper for read-mostly master tables. */
async function selectAll<T>(table: string, orderBy?: string): Promise<T[]> {
  assertConfigured();
  const query = getSupabase().from(table).select("*");
  const { data, error } = orderBy
    ? await query.order(orderBy)
    : await query;
  if (error) throw new Error(`[db] ${table} select failed: ${error.message}`);
  return (data ?? []) as T[];
}

// --- master data repos -------------------------------------------------------

export const countriesRepo = {
  list: () => selectAll<Country>("countries", "name_en"),
  async get(id: string): Promise<Country | null> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("countries")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(`[db] countries.get: ${error.message}`);
    return (data as Country) ?? null;
  },
};

export const incotermsRepo = {
  list: () => selectAll<Incoterm>("incoterms", "id"),
};

export const paymentTermsRepo = {
  list: () => selectAll<PaymentTerm>("payment_terms", "code"),
};

/** Generic insert helper returning the created row. */
async function insertRow<T>(table: string, row: Record<string, unknown>): Promise<T> {
  assertConfigured();
  const { data, error } = await getSupabase()
    .from(table)
    .insert(row)
    .select()
    .single();
  if (error) throw new Error(`[db] ${table} insert failed: ${error.message}`);
  return data as T;
}

/** Generic update-by-id helper returning the updated row. */
async function updateRow<T>(
  table: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<T> {
  assertConfigured();
  const { data, error } = await getSupabase()
    .from(table)
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(`[db] ${table} update failed: ${error.message}`);
  return data as T;
}

/** Product create input (id/created_at are DB-generated). */
export type NewProduct = NewRow<Product, "id" | "created_at">;
export const productsRepo = {
  list: () => selectAll<Product>("products", "model_name"),
  create: (row: NewProduct) => insertRow<Product>("products", row),
  update: (id: string, patch: Partial<NewProduct>) =>
    updateRow<Product>("products", id, patch),
};

/** Partner create input. */
export type NewPartner = NewRow<Partner, "id" | "created_at">;
export const partnersRepo = {
  list: () => selectAll<Partner>("partners", "company_name"),
  create: (row: NewPartner) => insertRow<Partner>("partners", row),
  update: (id: string, patch: Partial<NewPartner>) =>
    updateRow<Partner>("partners", id, patch),
};

/** Shipper-profile create input. */
export type NewShipperProfile = NewRow<ShipperProfile, "id" | "created_at">;
export const shipperProfilesRepo = {
  list: () => selectAll<ShipperProfile>("shipper_profiles", "company_name"),
  create: (row: NewShipperProfile) =>
    insertRow<ShipperProfile>("shipper_profiles", row),
  update: (id: string, patch: Partial<NewShipperProfile>) =>
    updateRow<ShipperProfile>("shipper_profiles", id, patch),
};

// --- deal workspace ----------------------------------------------------------

/** Deal create input (id/created_at/updated_at are DB-generated). */
export type NewDeal = NewRow<Deal, "id" | "created_at" | "updated_at">;

export const dealsRepo = {
  list: () => selectAll<Deal>("deals", "created_at"),
  async get(id: string): Promise<Deal | null> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("deals")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(`[db] deals.get: ${error.message}`);
    return (data as Deal) ?? null;
  },
  async lineItems(dealId: string): Promise<DealLineItem[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("deal_line_items")
      .select("*")
      .eq("deal_id", dealId)
      .order("line_no");
    if (error) throw new Error(`[db] deal_line_items: ${error.message}`);
    return (data ?? []) as DealLineItem[];
  },
  create: (row: NewDeal) => insertRow<Deal>("deals", row),
  update: (id: string, patch: Partial<NewDeal>) =>
    updateRow<Deal>("deals", id, patch),
};

/** Line-item create input. */
export type NewDealLineItem = NewRow<DealLineItem, "id" | "created_at">;

export const dealLineItemsRepo = {
  /** Bulk-insert the line items for a deal. */
  async createMany(rows: NewDealLineItem[]): Promise<DealLineItem[]> {
    assertConfigured();
    if (rows.length === 0) return [];
    const { data, error } = await getSupabase()
      .from("deal_line_items")
      .insert(rows)
      .select();
    if (error)
      throw new Error(`[db] deal_line_items insert failed: ${error.message}`);
    return (data ?? []) as DealLineItem[];
  },
};

/** Document record create input. */
export type NewDocumentRecord = NewRow<DocumentRecord, "id" | "created_at">;
/** Document version create input (immutable snapshot, DP-5). */
export type NewDocumentVersion = NewRow<DocumentVersion, "id" | "issued_at">;

export const documentsRepo = {
  list(dealId: string): Promise<DocumentRecord[]> {
    return (async () => {
      assertConfigured();
      const { data, error } = await getSupabase()
        .from("documents")
        .select("*")
        .eq("deal_id", dealId)
        .order("created_at");
      if (error) throw new Error(`[db] documents.list: ${error.message}`);
      return (data ?? []) as DocumentRecord[];
    })();
  },
  create: (row: NewDocumentRecord) =>
    insertRow<DocumentRecord>("documents", row),
  /** Insert an immutable issued version snapshot (DP-5). */
  saveVersion: (row: NewDocumentVersion) =>
    insertRow<DocumentVersion>("document_versions", row),
};

// --- v2 (export-workflow-v2) ---------------------------------------------------

export const workflowStagesRepo = {
  list: () => selectAll<WorkflowStage>("workflow_stages", "sort"),
};

export const dealPartiesRepo = {
  async listByDeal(dealId: string): Promise<DealParty[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("deal_parties")
      .select("*")
      .eq("deal_id", dealId);
    if (error) throw new Error(`[db] deal_parties.list: ${error.message}`);
    return (data ?? []) as DealParty[];
  },
  /** Assign a partner to a role on a deal (one partner per role — upsert). */
  async set(dealId: string, role: string, partnerId: string): Promise<DealParty> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("deal_parties")
      .upsert(
        { deal_id: dealId, role, partner_id: partnerId },
        { onConflict: "deal_id,role" },
      )
      .select()
      .single();
    if (error) throw new Error(`[db] deal_parties.set: ${error.message}`);
    return data as DealParty;
  },
  async remove(dealId: string, role: string): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase()
      .from("deal_parties")
      .delete()
      .eq("deal_id", dealId)
      .eq("role", role);
    if (error) throw new Error(`[db] deal_parties.remove: ${error.message}`);
  },
};

export const checklistTemplatesRepo = {
  async listByCountry(countryId: string): Promise<ChecklistTemplate[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("checklist_templates")
      .select("*")
      .contains("country_scope", [countryId])
      .order("sort");
    if (error) throw new Error(`[db] checklist_templates: ${error.message}`);
    return (data ?? []) as ChecklistTemplate[];
  },
};

export const checklistItemsRepo = {
  async listByDeal(dealId: string): Promise<ChecklistItem[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("checklist_items")
      .select("*")
      .eq("deal_id", dealId);
    if (error) throw new Error(`[db] checklist_items.list: ${error.message}`);
    return (data ?? []) as ChecklistItem[];
  },
  /** Instantiate missing per-deal items from the country templates (lazy, idempotent). */
  async ensureForDeal(
    dealId: string,
    templates: ChecklistTemplate[],
  ): Promise<ChecklistItem[]> {
    const existing = await this.listByDeal(dealId);
    const have = new Set(existing.map((i) => i.item_key));
    const missing = templates.filter((t) => !have.has(t.item_key));
    if (missing.length === 0) return existing;
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("checklist_items")
      .insert(
        missing.map((t) => ({
          deal_id: dealId,
          item_key: t.item_key,
          label: t.label,
          status: "pending",
        })),
      )
      .select();
    if (error) throw new Error(`[db] checklist_items.ensure: ${error.message}`);
    return [...existing, ...((data ?? []) as ChecklistItem[])];
  },
  async setStatus(id: string, status: ChecklistItem["status"]): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase()
      .from("checklist_items")
      .update({
        status,
        resolved_at: status === "done" ? new Date().toISOString() : null,
      })
      .eq("id", id);
    if (error) throw new Error(`[db] checklist_items.setStatus: ${error.message}`);
  },
};

export const knowledgeRepo = {
  list: () => selectAll<KnowledgeArticle>("knowledge_articles", "sort"),
};

// --- v3 (unified-trade-flow) ---------------------------------------------------

/** 도구 상태 blob (마진 시뮬레이터 DeskState 등) — key 단위 GET/PUT. */
export const appStateRepo = {
  async get(key: string): Promise<AppStateRow | null> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("app_state")
      .select("*")
      .eq("key", key)
      .maybeSingle();
    if (error) throw new Error(`[db] app_state.get: ${error.message}`);
    return (data as AppStateRow) ?? null;
  },
  async put(key: string, value: unknown): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase()
      .from("app_state")
      .upsert({ key, data: value }, { onConflict: "key" });
    if (error) throw new Error(`[db] app_state.put: ${error.message}`);
  },
};

/** Contract create input (id/created_at/updated_at are DB-generated). */
export type NewContract = NewRow<ContractRow, "id" | "created_at" | "updated_at">;

export const contractsRepo = {
  list: () => selectAll<ContractRow>("contracts", "created_at"),
  async get(id: string): Promise<ContractRow | null> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("contracts")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(`[db] contracts.get: ${error.message}`);
    return (data as ContractRow) ?? null;
  },
  create: (row: NewContract) => insertRow<ContractRow>("contracts", row),
  update: (id: string, patch: Partial<NewContract>) =>
    updateRow<ContractRow>("contracts", id, patch),
  async remove(id: string): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase().from("contracts").delete().eq("id", id);
    if (error) throw new Error(`[db] contracts.remove: ${error.message}`);
  },
};

/** Contract-sale create input. */
export type NewContractSale = NewRow<ContractSale, "id" | "created_at">;

export const contractSalesRepo = {
  list: () => selectAll<ContractSale>("contract_sales", "sale_date"),
  async listByContract(contractId: string): Promise<ContractSale[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("contract_sales")
      .select("*")
      .eq("contract_id", contractId)
      .order("sale_date");
    if (error) throw new Error(`[db] contract_sales.list: ${error.message}`);
    return (data ?? []) as ContractSale[];
  },
  create: (row: NewContractSale) => insertRow<ContractSale>("contract_sales", row),
  update: (id: string, patch: Partial<NewContractSale>) =>
    updateRow<ContractSale>("contract_sales", id, patch),
  async remove(id: string): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase().from("contract_sales").delete().eq("id", id);
    if (error) throw new Error(`[db] contract_sales.remove: ${error.message}`);
  },
};

// --- v4 (distributor-desk) -----------------------------------------------------
// 총판 관계 레이어. 기존 partners/contracts/deals/countries 는 위 repo 를 재사용하고,
// 여기서는 관계·자료·마케팅·개정·이슈만 다룬다. 불변 테이블(contract_amendments,
// distributor_issue_events)은 create/append 만 노출 (INSERT-only RLS 일치).

/** 총판 관계 create 입력. */
export type NewDistributorRelationship = NewRow<
  DistributorRelationship,
  "id" | "created_at" | "updated_at"
>;

export const distributorRelationshipsRepo = {
  list: () => selectAll<DistributorRelationship>("distributor_relationships", "country_id"),
  async listByCountry(countryId: string): Promise<DistributorRelationship[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("distributor_relationships")
      .select("*")
      .eq("country_id", countryId)
      .order("created_at");
    if (error) throw new Error(`[db] distributor_relationships.listByCountry: ${error.message}`);
    return (data ?? []) as DistributorRelationship[];
  },
  create: (row: NewDistributorRelationship) =>
    insertRow<DistributorRelationship>("distributor_relationships", row),
  update: (id: string, patch: Partial<NewDistributorRelationship>) =>
    updateRow<DistributorRelationship>("distributor_relationships", id, patch),
};

/** 공유 자산 create 입력. */
export type NewDistributorAsset = NewRow<DistributorAsset, "id" | "created_at" | "updated_at">;
/** 자산 버전 create 입력 (version 은 호출측이 지정). */
export type NewDistributorAssetVersion = NewRow<DistributorAssetVersion, "id" | "uploaded_at">;

export const distributorAssetsRepo = {
  list: () => selectAll<DistributorAsset>("distributor_assets", "title"),
  create: (row: NewDistributorAsset) => insertRow<DistributorAsset>("distributor_assets", row),
  update: (id: string, patch: Partial<NewDistributorAsset>) =>
    updateRow<DistributorAsset>("distributor_assets", id, patch),
  async versions(assetId: string): Promise<DistributorAssetVersion[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("distributor_asset_versions")
      .select("*")
      .eq("asset_id", assetId)
      .order("version");
    if (error) throw new Error(`[db] distributor_asset_versions.list: ${error.message}`);
    return (data ?? []) as DistributorAssetVersion[];
  },
  /** 새 버전 append + 자산의 current_version 이동 (교체). */
  async addVersion(row: NewDistributorAssetVersion): Promise<DistributorAssetVersion> {
    const created = await insertRow<DistributorAssetVersion>("distributor_asset_versions", row);
    await updateRow<DistributorAsset>("distributor_assets", row.asset_id, {
      current_version: row.version,
    });
    return created;
  },
};

/** 제공 이력 create 입력. */
export type NewDistributorProvision = NewRow<DistributorProvision, "id" | "created_at">;

export const distributorProvisionsRepo = {
  list: () => selectAll<DistributorProvision>("distributor_provisions", "provided_at"),
  async listByPartner(partnerId: string): Promise<DistributorProvision[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("distributor_provisions")
      .select("*")
      .eq("partner_id", partnerId)
      .order("provided_at");
    if (error) throw new Error(`[db] distributor_provisions.listByPartner: ${error.message}`);
    return (data ?? []) as DistributorProvision[];
  },
  create: (row: NewDistributorProvision) =>
    insertRow<DistributorProvision>("distributor_provisions", row),
};

/** 마케팅 포스트 create 입력. */
export type NewMarketingPost = NewRow<MarketingPost, "id" | "created_at" | "updated_at">;
/** 배포 타겟 create 입력. */
export type NewMarketingPostTarget = NewRow<MarketingPostTarget, "id" | "created_at">;

export const marketingPostsRepo = {
  list: () => selectAll<MarketingPost>("marketing_posts", "created_at"),
  create: (row: NewMarketingPost) => insertRow<MarketingPost>("marketing_posts", row),
  update: (id: string, patch: Partial<NewMarketingPost>) =>
    updateRow<MarketingPost>("marketing_posts", id, patch),
  async remove(id: string): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase().from("marketing_posts").delete().eq("id", id);
    if (error) throw new Error(`[db] marketing_posts.remove: ${error.message}`);
  },
  async targets(postId: string): Promise<MarketingPostTarget[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("marketing_post_targets")
      .select("*")
      .eq("post_id", postId)
      .order("created_at");
    if (error) throw new Error(`[db] marketing_post_targets.list: ${error.message}`);
    return (data ?? []) as MarketingPostTarget[];
  },
  addTarget: (row: NewMarketingPostTarget) =>
    insertRow<MarketingPostTarget>("marketing_post_targets", row),
  updateTarget: (id: string, patch: Partial<NewMarketingPostTarget>) =>
    updateRow<MarketingPostTarget>("marketing_post_targets", id, patch),
  async removeTarget(id: string): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase().from("marketing_post_targets").delete().eq("id", id);
    if (error) throw new Error(`[db] marketing_post_targets.remove: ${error.message}`);
  },
};

export const contractAmendmentsRepo = {
  list: () => selectAll<ContractAmendment>("contract_amendments", "amended_at"),
  async listByContract(contractId: string): Promise<ContractAmendment[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("contract_amendments")
      .select("*")
      .eq("contract_id", contractId)
      .order("amendment_no");
    if (error) throw new Error(`[db] contract_amendments.listByContract: ${error.message}`);
    return (data ?? []) as ContractAmendment[];
  },
  /**
   * 계약 개정을 원자적으로 적용 — contracts 갱신 + amendment append 를 단일
   * Postgres 트랜잭션(RPC)으로. 두 번째 쓰기 실패 시 전체 롤백 (고아 개정 방지).
   */
  async apply(
    contractId: string,
    patch: ContractAmendmentPatch,
    amendment: ContractAmendmentInput,
  ): Promise<ContractAmendment> {
    assertConfigured();
    const { data, error } = await getSupabase().rpc("apply_contract_amendment", {
      p_contract_id: contractId,
      p_patch: patch,
      p_amendment: amendment,
    });
    if (error) throw new Error(`[db] apply_contract_amendment: ${error.message}`);
    return data as ContractAmendment;
  },
};

/** 이슈 create 입력. */
export type NewDistributorIssue = NewRow<DistributorIssue, "id" | "created_at" | "updated_at">;
/** 이슈 조치 로그 create 입력 (append-only, event_at 은 DB 기본값 허용). */
export type NewDistributorIssueEvent = NewRow<
  DistributorIssueEvent,
  "id" | "created_at" | "event_at"
>;

export const distributorIssuesRepo = {
  list: () => selectAll<DistributorIssue>("distributor_issues", "opened_at"),
  async get(id: string): Promise<DistributorIssue | null> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("distributor_issues")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(`[db] distributor_issues.get: ${error.message}`);
    return (data as DistributorIssue) ?? null;
  },
  create: (row: NewDistributorIssue) => insertRow<DistributorIssue>("distributor_issues", row),
  update: (id: string, patch: Partial<NewDistributorIssue>) =>
    updateRow<DistributorIssue>("distributor_issues", id, patch),
  async events(issueId: string): Promise<DistributorIssueEvent[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("distributor_issue_events")
      .select("*")
      .eq("issue_id", issueId)
      .order("event_at");
    if (error) throw new Error(`[db] distributor_issue_events.list: ${error.message}`);
    return (data ?? []) as DistributorIssueEvent[];
  },
  addEvent: (row: NewDistributorIssueEvent) =>
    insertRow<DistributorIssueEvent>("distributor_issue_events", row),
};

// --- v5 (contract-timeline) ----------------------------------------------------
// 계약 런칭 프로그램 타임라인. Design: docs/02-design/contract-timeline.design.md §4.4.
// 불변원칙:
//  - 진행상태 컬럼 없음(§3) — `deals.status` 가 단일 출처. 이벤트는 `deal_id` 로 링크만 한다.
//  - 보드는 `contracts.*` 날짜를 UPDATE 하지 않는다(§2) — 여기 repo 에 계약 쓰기 경로 없음.
//  - `contract_timeline_event_moves` 는 불변 — 트리거가 적재하고 repo 는 `moves` 로 읽기만 한다(§4.0).
//  - `update` 는 `rev` 프리컨디션 필수(§7.2 탭 2개) — `updateRow`(=`.eq("id")` 만) 사용 금지.
//  - `rev` 는 `bump_timeline_rev` 트리거가 증가시킨다 — patch 에 넣지 않는다(타입이 강제).

/** 레인 마스터 — 시드 DATA(7행), 읽기 전용 (DP-2). */
export const timelineLanesRepo = {
  list: () => selectAll<TimelineLane>("timeline_lanes", "sort"),
};

/**
 * 이벤트 create 입력. `rev` 는 DB 트리거 소유이므로 `id`/타임스탬프와 함께 제외한다 —
 * 이 타입이 곧 `update` patch 에 `rev` 가 섞이지 못하게 하는 컴파일 시점 방어다.
 */
export type NewTimelineEvent = NewRow<
  ContractTimelineEvent,
  "id" | "rev" | "created_at" | "updated_at"
>;

export const timelineEventsRepo = {
  /** 계약 1건의 이벤트 전량. 레인 내 표시 순서 = `sort` → 동률이면 `start_date` (§4.4). */
  async listByContract(contractId: string): Promise<ContractTimelineEvent[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("contract_timeline_events")
      .select("*")
      .eq("contract_id", contractId)
      .order("sort")
      .order("start_date");
    if (error)
      throw new Error(`[db] contract_timeline_events select failed: ${error.message}`);
    return (data ?? []) as ContractTimelineEvent[];
  },

  /** 이벤트 생성. 기하 무결성(마일스톤 = `end_date` null)은 DB CHECK 가 최종 판정한다. */
  create: (row: NewTimelineEvent) =>
    insertRow<ContractTimelineEvent>("contract_timeline_events", row),

  /**
   * 낙관적 잠금 UPDATE (§7.2). `rev` 를 WHERE 에 넣어 읽은 이후 다른 탭이 손댔으면 0행이 된다.
   * 0행 = 충돌 → throw. 호출측은 이 에러에서 좌표를 롤백하고 보드를 락한다(§7.1).
   */
  async update(
    id: string,
    rev: number,
    patch: Partial<NewTimelineEvent>,
  ): Promise<ContractTimelineEvent> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("contract_timeline_events")
      .update(patch)
      .eq("id", id)
      .eq("rev", rev)
      .select()
      .maybeSingle();
    if (error)
      throw new Error(`[db] contract_timeline_events update failed: ${error.message}`);
    if (!data)
      throw new Error(
        "[db] contract_timeline_events update failed: 다른 탭에서 변경됨 (새로고침 필요)",
      );
    return data as ContractTimelineEvent;
  },

  /** 이벤트 삭제. 이동 이력은 FK `on delete cascade` 로 함께 사라진다(§4.1). */
  async remove(id: string): Promise<void> {
    assertConfigured();
    const { error } = await getSupabase()
      .from("contract_timeline_events")
      .delete()
      .eq("id", id);
    if (error)
      throw new Error(`[db] contract_timeline_events delete failed: ${error.message}`);
  },

  /** 이동 이력 (불변, 읽기 전용). 최신순 — [0] 이 undo 1단의 소스다(§7.3). */
  async moves(eventId: string): Promise<TimelineEventMove[]> {
    assertConfigured();
    const { data, error } = await getSupabase()
      .from("contract_timeline_event_moves")
      .select("*")
      .eq("event_id", eventId)
      .order("moved_at", { ascending: false });
    if (error)
      throw new Error(
        `[db] contract_timeline_event_moves select failed: ${error.message}`,
      );
    return (data ?? []) as TimelineEventMove[];
  },
};
